import * as models from 'smg/customer-api/models';
import Konva from 'konva';
import { CustomizerEngine, ImageLayer } from 'smg/customizer';
import { CanvasSize, ShapeClipFunc } from './geometry';

const LAYER_ID = 'pin-shading';

// Indent seam at every ink→plating transition: the raised print sits proud of the bare
// metal, so transparent areas show a slight shadow at the boundary (see the OBE gallery
// pin). Applied as Konva-native shadows on each art node (InkShadow below) so the seam
// moves in perfect sync with drags/scales/undo — no recompute, no staleness.
const INK_SHADOW_COLOR = '#141414';
const INK_SHADOW_BLUR_PX = 5;
const INK_SHADOW_OPACITY = 0.9;
// Depth reads through direction, not darkness: light from the top-left, so the raised
// print casts down-right into the bare-plating recesses (matches the product photos)
const INK_SHADOW_OFFSET = { x: 2, y: 4 };

// The rim is dual-tone like a real metal edge — a soft dark falloff inside plus a thin
// bright bevel line right at the edge (the highlight is what defines the silhouette over
// dark art, where a shadow disappears).
const RIM_WIDTH_PX = 6;
const RIM_BLUR_PX = 3;
const RIM_ALPHA = 0.65;
const BEVEL_WIDTH_PX = 2;
const BEVEL_ALPHA = 0.3;
const RIM_SHADOW_COLOR = '28, 28, 28';

/** Apply the raised-print indent shadow to an art node (uploads and text blocks) */
export function InkShadow(node: Konva.Shape): void {
    node.shadowColor(INK_SHADOW_COLOR);
    node.shadowBlur(INK_SHADOW_BLUR_PX);
    node.shadowOpacity(INK_SHADOW_OPACITY);
    node.shadowOffset(INK_SHADOW_OFFSET);
    node.shadowEnabled(true);
}

/**
 * The pin-edge rim overlay: makes an edge-to-edge design read as a physical object
 * instead of a flat vector shape. Depends ONLY on the shape/size geometry — computed
 * once per attachTo and never again, so it cannot go stale. Presentation only: never
 * printed, never persisted, not hit-testable.
 */
export class PinShadingOverlay {
    #engine: CustomizerEngine | null = null;
    #layer: ImageLayer | null = null;

    /** Bind to a freshly-created engine after a shape/size switch */
    async attachTo(engine: CustomizerEngine, sizeInit: models.ILineItemTypeSize): Promise<void> {
        const size = new models.LineItemTypeSize(sizeInit);
        this.#engine = engine;

        this.#layer = new ImageLayer(LAYER_ID, { userEditable: false, resizable: false });
        await this.#layer.setImage(this.#renderRim(size));
        await engine.addLayer(LAYER_ID, this.#layer);
        engine.bringLayerToFront(LAYER_ID);
        // Non-background images load centered against the coin's hardcoded 969 canvas —
        // place the overlay (center-offset node) at THIS canvas's center
        const { width, height } = CanvasSize(size);
        this.#layer.setPosition(width / 2, height / 2);
        // Purely decorative — clicks must fall through to the art below
        this.#layer.getImageNode()?.listening(false);
        this.#layer.getImageNode()?.getLayer()?.listening(false);
        engine.batchDraw();

        // New uploads bring themselves to the front — stay above them. Re-fronting is
        // cheap (no re-render; the rim never changes for this engine's lifetime).
        engine.on('significantChange', data => {
            if (data.layerId === LAYER_ID || !this.#engine) {
                return;
            }
            this.#engine.bringLayerToFront(LAYER_ID);
        });
    }

    /**
     * Run an export with all shading suppressed — the rim overlay and ink shadows are
     * presentation, not print: shadow pixels sit OUTSIDE the ink and would fatten the
     * white layer's alpha threshold, and the rim would bake into the color layer.
     */
    async withHidden<T>(fn: () => Promise<T>): Promise<T> {
        const engine = this.#engine;
        if (!engine) {
            return fn();
        }
        const restore: (() => void)[] = [];
        for (const konvaLayer of engine.getStage().getLayers()) {
            const id = konvaLayer.name() || '';
            if (id === LAYER_ID) {
                if (konvaLayer.visible()) {
                    konvaLayer.visible(false);
                    restore.push(() => konvaLayer.visible(true));
                }
                continue;
            }
            if (!id.startsWith('img-') && !id.startsWith('text-drop-')) {
                continue;
            }
            for (const node of konvaLayer.getChildren()) {
                if (node instanceof Konva.Shape && node.shadowEnabled()) {
                    node.shadowEnabled(false);
                    restore.push(() => node.shadowEnabled(true));
                }
            }
        }
        engine.batchDraw();
        try {
            return await fn();
        } finally {
            for (const restoreOne of restore) {
                restoreOne();
            }
            engine.batchDraw();
        }
    }

    /** Compose the rim: dark falloff + bright bevel line along the shape path */
    #renderRim(size: models.LineItemTypeSize): string {
        const { width, height } = CanvasSize(size);
        const $overlay = document.createElement('canvas');
        $overlay.width = width;
        $overlay.height = height;
        const ctx = $overlay.getContext('2d');
        if (!ctx) {
            return '';
        }

        const clipPath = ShapeClipFunc(size);
        ctx.save();
        ctx.beginPath();
        clipPath(ctx);
        ctx.clip();
        ctx.beginPath();
        clipPath(ctx);
        ctx.lineWidth = RIM_WIDTH_PX * 2; // half falls outside the clip
        ctx.strokeStyle = `rgba(${RIM_SHADOW_COLOR}, ${RIM_ALPHA})`;
        ctx.filter = `blur(${RIM_BLUR_PX}px)`;
        ctx.stroke();
        ctx.beginPath();
        clipPath(ctx);
        ctx.lineWidth = BEVEL_WIDTH_PX * 2;
        ctx.strokeStyle = `rgba(255, 255, 255, ${BEVEL_ALPHA})`;
        ctx.filter = 'blur(1px)';
        ctx.stroke();
        ctx.restore();

        // Nothing may draw outside the pin
        ctx.filter = 'none';
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        clipPath(ctx);
        ctx.fill();

        return $overlay.toDataURL();
    }
}
